import React, { useRef, useEffect, useState } from 'react';
import Button from '../components/Button';
import { GameState } from '../types';
import { Play, RotateCcw, Ban } from 'lucide-react';

const Dojo: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [gameState, setGameState] = useState<GameState>(GameState.IDLE);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);

  // Game Loop Refs
  const requestRef = useRef<number>(0);
  const entityRef = useRef({ x: 150, y: 150, radius: 30, visible: false, timestamp: 0 });
  
  const startGame = () => {
    setGameState(GameState.PLAYING);
    setScore(0);
    setTimeLeft(30);
    entityRef.current = { x: 200, y: 200, radius: 30, visible: true, timestamp: Date.now() };
  };

  useEffect(() => {
    if (gameState !== GameState.PLAYING) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setGameState(GameState.GAME_OVER);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [gameState]);

  // Draw Loop
  const animate = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear - Paper Background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw Blue Notebook Lines
    ctx.strokeStyle = '#a0a0ff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for(let i=40; i<canvas.height; i+=40) {
        ctx.moveTo(0, i);
        ctx.lineTo(canvas.width, i);
    }
    ctx.stroke();

    // Draw Pink Margin (He's fancy)
    ctx.strokeStyle = '#ff99cc';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(40, 0);
    ctx.lineTo(40, canvas.height);
    ctx.stroke();

    if (gameState === GameState.PLAYING && entityRef.current.visible) {
      const { x, y } = entityRef.current;
      
      // Auto move Olek frantically (He is a wimp)
      if (Date.now() - entityRef.current.timestamp > 700) {
         entityRef.current.x = Math.random() * (canvas.width - 100) + 50;
         entityRef.current.y = Math.random() * (canvas.height - 100) + 50;
         entityRef.current.timestamp = Date.now();
      }

      // Draw Stickman Olek (Petite, Pink suit)
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // Head
      ctx.beginPath();
      ctx.arc(x, y, 20, 0, Math.PI * 2);
      ctx.stroke();
      
      // Pink Hair (Receding) - thin strands
      ctx.strokeStyle = '#ff66aa';
      ctx.beginPath();
      ctx.moveTo(x-18, y-5); 
      ctx.quadraticCurveTo(x-10, y-25, x, y-20);
      ctx.moveTo(x+18, y-5); 
      ctx.quadraticCurveTo(x+10, y-25, x, y-20);
      ctx.stroke();
      ctx.strokeStyle = '#000000';

      // Face (Insulting/Smug)
      ctx.beginPath();
      ctx.arc(x-7, y-5, 2, 0, Math.PI*2); // Eye L
      ctx.arc(x+7, y-5, 2, 0, Math.PI*2); // Eye R
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(x-10, y+5);
      ctx.quadraticCurveTo(x, y+15, x+10, y+5); // Smug smile
      ctx.stroke();

      // Body (Pink Suit)
      ctx.strokeStyle = '#ff99cc'; 
      ctx.lineWidth = 6;
      ctx.beginPath();
      ctx.moveTo(x, y + 20);
      ctx.lineTo(x, y + 70);
      ctx.stroke();
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#000000';

      // Arms (Flailing - wimp)
      ctx.beginPath();
      ctx.moveTo(x, y + 30);
      ctx.lineTo(x - 25, y + 15);
      ctx.moveTo(x, y + 30);
      ctx.lineTo(x + 25, y + 15);
      ctx.stroke();

      // Legs
      ctx.beginPath();
      ctx.moveTo(x, y + 70);
      ctx.lineTo(x - 15, y + 110);
      ctx.moveTo(x, y + 70);
      ctx.lineTo(x + 15, y + 110);
      ctx.stroke();
    }

    requestRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current!);
  }, [gameState]);

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (gameState !== GameState.PLAYING) return;
    
    const rect = canvasRef.current!.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    const sm = entityRef.current;
    // Hitbox
    if (clickX > sm.x - 40 && clickX < sm.x + 40 && clickY > sm.y - 40 && clickY < sm.y + 120) {
      setScore(prev => prev + 1);
      // Teleport immediately
      entityRef.current.x = Math.random() * (canvasRef.current!.width - 100) + 50;
      entityRef.current.y = Math.random() * (canvasRef.current!.height - 100) + 50;
      entityRef.current.timestamp = Date.now();
    }
  };

  return (
    <div className="flex flex-col items-center gap-8">
      <div className="text-center">
        <h1 className="text-4xl font-black lowercase text-olek-pink-dark">Tax Evasion</h1>
        <p className="font-bold text-gray-500">slap him (he deserves it)</p>
      </div>
      
      <div className="relative border-4 border-black shadow-hard select-none">
        
        {/* HUD */}
        <div className="absolute top-4 left-4 right-4 flex justify-between font-bold text-xl z-10 pointer-events-none">
          <div className="bg-white border-2 border-black px-2 shadow-sm">Slaps: {score}</div>
          <div className={`bg-white border-2 border-black px-2 shadow-sm ${timeLeft < 5 ? 'text-red-600' : ''}`}>
            IRS Arrival: {timeLeft}s
          </div>
        </div>

        <canvas 
          ref={canvasRef}
          width={600}
          height={400}
          className="w-full max-w-2xl cursor-crosshair active:cursor-grabbing bg-white"
          onClick={handleCanvasClick}
        />
        
        {gameState === GameState.IDLE && (
          <div className="absolute inset-0 bg-white/50 flex flex-col items-center justify-center backdrop-blur-sm z-20">
            <Button onClick={startGame} className="animate-bounce">
              <Play size={20} className="inline mr-2"/> Audit Him
            </Button>
          </div>
        )}

         {gameState === GameState.GAME_OVER && (
          <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center gap-6 backdrop-blur-sm z-20">
             <div className="text-center transform rotate-2">
              <h2 className="text-5xl font-black text-red-600">AUDIT COMPLETE</h2>
              <p className="text-2xl font-bold">You caught him {score} times</p>
            </div>
            <Button onClick={startGame} variant="secondary">
              <RotateCcw size={20} className="inline mr-2"/> Re-Audit
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dojo;